import React, { useState, useEffect } from 'react';
import { toast , ToastContainer} from 'react-toastify';
import { RegularButton } from '../../../common/ReusableComponent/CustomButton';
import Permissions from '../../../../config/permissions';
import './FeatureSelectionModal.css';


const normalize = (str) => str?.toLowerCase().trim();
const FeatureSelectionModal = ({ show, onClose, onSave, userFormData }) => {
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [, setAvailableFeatures] = useState([]);
  const [showSubPermissions, setShowSubPermissions] = useState({});
  const [selectedPermissionCodes, setSelectedPermissionCodes] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  const allFeatures = Object.values(Permissions);
  const allFeatureLabels = allFeatures.map(f => f.label);
  const allPermissionCodes = allFeatures.flatMap(f => [
    f.code,
    ...(f.children?.map(c => c.code) || [])
  ]);

  const isAllSelected = allFeatureLabels.every(l => selectedFeatures.includes(l));

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedFeatures([]);
      setSelectedPermissionCodes([]);
      setShowSubPermissions({});
    } else {
      setSelectedFeatures(allFeatureLabels);
      setSelectedPermissionCodes(allPermissionCodes);
      const expanded = {};
      allFeatures.forEach(f => { if (f.children?.length) expanded[f.label] = true; });
      setShowSubPermissions(expanded);
    }
  };



useEffect(() => {
  if (show && userFormData) {
    try {
      const key = Object.keys(localStorage).find(k => k.includes('checkUserType'));
      const cachedData = key ? JSON.parse(localStorage.getItem(key)) : null;
      const matchingTitlesRaw = cachedData?.matching_features_titles || [];
      const matchingTitles = matchingTitlesRaw.map(normalize);
      const matchedPermissions = Object.values(Permissions).filter(perm =>
        matchingTitles.includes(normalize(perm.label))
      );
      const matchedLabels = matchedPermissions.map(p => p.label);
      setAvailableFeatures(matchedLabels);
      const previousFeatures = userFormData?.previouslySelectedFeatures || [];
      const previousPermissions = userFormData?.previouslySelectedPermissions || [];
      const apiPermissions = userFormData?.apiPermissions || userFormData?.previouslySelectedPermissions || [];
      const editingMode = userFormData?.isEditing || false;
      let finalSelectedFeatures = [];
      let finalSelectedPermissions = [];
      if (editingMode && apiPermissions.length > 0) {      
        Object.values(Permissions).forEach(feature => {
          const hasMainFeatureCode = apiPermissions.includes(feature.code);
          const hasChildPermissions = feature.children?.some(child => 
            apiPermissions.includes(child.code)
          );
          if (hasMainFeatureCode || hasChildPermissions) {
            finalSelectedFeatures.push(feature.label);
            if (hasMainFeatureCode) {
              finalSelectedPermissions.push(feature.code);
            }
            if (feature.children) {
              feature.children.forEach(child => {
                if (apiPermissions.includes(child.code)) {
                  finalSelectedPermissions.push(child.code);
                }
              });
            }
          }
        });
        finalSelectedFeatures = [...new Set(finalSelectedFeatures)];
        finalSelectedPermissions = [...new Set(finalSelectedPermissions)];
      } else {
        finalSelectedFeatures = [...previousFeatures];
        finalSelectedPermissions = [...previousPermissions];
      }
      setIsEditing(editingMode);
      setSelectedFeatures(finalSelectedFeatures);
      setSelectedPermissionCodes(finalSelectedPermissions);
      const expanded = {};
      Object.values(Permissions).forEach((perm) => {
        if (
          finalSelectedFeatures.includes(perm.label) ||
          (perm.children && perm.children.some(child => finalSelectedPermissions.includes(child.code)))
        ) {
          expanded[perm.label] = true;
        }
      });
      setShowSubPermissions(expanded);
    } catch (error) {
      console.error('Error loading features:', error);
      toast.error('Error loading features');
      setAvailableFeatures([]);
      setSelectedFeatures([]);
      setSelectedPermissionCodes([]);
    }
  }
}, [show, userFormData]);


const handleFeatureToggle = (featureLabel) => {
  const feature = Object.values(Permissions).find(p => p.label === featureLabel);
  if (!feature) return;
  const isSelected = selectedFeatures.includes(featureLabel);
  const newSelected = isSelected
    ? selectedFeatures.filter(f => f !== featureLabel)
    : [...selectedFeatures, featureLabel];
  setSelectedFeatures(newSelected);
  const childCodes = feature.children?.map(c => c.code) || [];
  if (!isSelected) {
    // select feature + all its children
    setShowSubPermissions(prev => ({ ...prev, [featureLabel]: true }));
    setSelectedPermissionCodes(prev => [...new Set([...prev, feature.code, ...childCodes])]);
  } else {
    // deselect feature + all its children
    setShowSubPermissions(prev => ({ ...prev, [featureLabel]: false }));
    setSelectedPermissionCodes(prev =>
      prev.filter(code => code !== feature.code && !childCodes.includes(code))
    );
  }
};


  const handleSubPermissionToggle = (code) => {
    setSelectedPermissionCodes(prev => {
      const newCodes = prev.includes(code)
        ? prev.filter(c => c !== code)
        : [...new Set([...prev, code])];
      return newCodes;
    });
  };

  const handleFeatureExpandToggle = (featureLabel, hasChildren) => {
    if (!hasChildren) return;
    setShowSubPermissions(prev => ({
      ...prev,
      [featureLabel]: !prev[featureLabel],
    }));
  };


const handleSave = () => {
  const hasSelectedFeatures = selectedFeatures.length > 0;
  const hasSelectedPermissions = selectedPermissionCodes.length > 0;
  if (!hasSelectedFeatures && !hasSelectedPermissions) {
    toast.warn('Please select at least one feature');
    return;
  }

  
  const finalPermissions = [...new Set(selectedPermissionCodes)];
  if (isEditing) {
    const prevFeatures = userFormData.previouslySelectedFeatures || [];
    const prevPermissions = userFormData.previouslySelectedPermissions || [];
    const isSameFeatures = 
      prevFeatures.length === selectedFeatures.length &&
      prevFeatures.every(f => selectedFeatures.includes(f));
    const isSamePermissions =
      prevPermissions.length === finalPermissions.length &&
      prevPermissions.every(p => finalPermissions.includes(p));
    if (isSameFeatures && isSamePermissions) {
      onClose(); 
      return;
    }
  }
  onSave(userFormData, selectedFeatures, finalPermissions);
};
  const handleCancel = () => {
    if (isEditing && userFormData) {
      setSelectedFeatures(userFormData.previouslySelectedFeatures || []);
      setSelectedPermissionCodes(userFormData.previouslySelectedPermissions || []);
    }
    onClose();
  };
  if (!show) return null;

  const totalPermCount = selectedPermissionCodes.length;

  return (
    <div className="fs-overlay">
      <ToastContainer />
      <div className="fs-modal">

        {/* HEADER */}
        <div className="fs-header">
          <div className="fs-header-left">
            <h2 className="fs-title">{isEditing ? 'Edit User Permissions' : 'Assign Permissions'}</h2>
            <p className="fs-subtitle">{isEditing ? 'Update features and permissions for this user' : 'Choose features and permissions for this user'}</p>
          </div>
          <button onClick={handleCancel} className="fs-close-btn" aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* TOOLBAR */}
        <div className="fs-toolbar">
          <div className="fs-toolbar-left">
            <label className="fs-select-all-label">
              <input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} />
              Select All Features
            </label>
            <span className="fs-count-badge">{selectedFeatures.length} / {allFeatures.length} features</span>
            <span className="fs-perm-count-badge">{totalPermCount} permissions</span>
          </div>
        </div>

        {/* CONTENT */}
        <div className="fs-content">
          <div className="fs-feature-grid">
            {allFeatures.map((feature, idx) => {
              const isFeatureSelected = selectedFeatures.includes(feature.label);
              const isExpanded = !!showSubPermissions[feature.label];
              const hasChildren = feature.children?.length > 0;
              const selectedChildCount = feature.children?.filter(c => selectedPermissionCodes.includes(c.code)).length || 0;
              return (
                <div key={idx} className={`fs-card${isFeatureSelected ? ' fs-card--selected' : ''}`}>
                  <div
                    className="fs-card-header"
                    onClick={() => handleFeatureExpandToggle(feature.label, hasChildren)}
                  >
                    <div className="fs-card-header-left">
                      <input
                        type="checkbox"
                        className="fs-card-checkbox"
                        checked={isFeatureSelected}
                        onChange={() => handleFeatureToggle(feature.label)}
                        onClick={e => e.stopPropagation()}
                      />
                      <span className="fs-card-title">{feature.label}</span>
                    </div>
                    <div className="fs-card-header-right">
                      {hasChildren && <span className="fs-sub-count">{selectedChildCount}/{feature.children.length}</span>}
                      {hasChildren && (
                        <button
                          type="button"
                          className="fs-chevron-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFeatureExpandToggle(feature.label, hasChildren);
                          }}
                          aria-label={isExpanded ? `Collapse ${feature.label}` : `Expand ${feature.label}`}
                        >
                          <svg className={`fs-chevron${isExpanded ? ' fs-chevron--open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                  {hasChildren && isExpanded && (
                    <div className="fs-children">
                      {feature.children.map((child, cIdx) => {
                        const isChildSelected = selectedPermissionCodes.includes(child.code);
                        return (
                          <div
                            key={cIdx}
                            className={`fs-child-item${isChildSelected ? ' fs-child--selected' : ''}`}
                            onClick={() => handleSubPermissionToggle(child.code)}
                          >
                            <input
                              type="checkbox"
                              className="fs-child-checkbox"
                              checked={isChildSelected}
                              onChange={() => handleSubPermissionToggle(child.code)}
                              onClick={e => e.stopPropagation()}
                            />
                            <span className="fs-child-label">{child.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* FOOTER */}
        <div className="fs-footer">
          <RegularButton text="Cancel" onClick={handleCancel} backgroundColor="#ffffff" color="#000000" />
          <RegularButton text={isEditing ? 'Update User' : 'Save User'} onClick={handleSave} backgroundColor="#282e26" color="#ffffff" />
        </div>

      </div>
    </div>
  );
};
export default FeatureSelectionModal;
